import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {BehaviorSubject, Subscription} from 'rxjs';
import {GateweeTableColumn} from '@gatewee/interfaces';
import {Recipient} from '@gatewee/types/recipient';
import {PaginatedResponse} from '@gatewee/types/system';
import {TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';

export type ProviderTransactionSearchTerm =
  | 'e2eId'
  | 'transactionId'
  | 'externalId'
  | 'idempotencyKey';

export interface GateweeTableModalState {
  recipient: Recipient;
  data: PaginatedResponse<unknown>;
  page: number;
  identifier: string;
  webhookResendEnabled: boolean;
}

export interface GateweeTableModalDialogData {
  state$: BehaviorSubject<GateweeTableModalState>;
  onSearchApply: (ev: {term: string; value: string}) => void;
  onNewPage: (page: string) => void;
  onResendWebhook: (ev: {
    row: Record<string, unknown>;
    selectedKey: string;
  }) => void;
}

@Component({
  selector: 'gatewee-web-gatewee-table-modal',
  templateUrl: './gatewee-table-modal.component.html',
  standalone: false,
})
export class GateweeTableModalComponent implements OnInit, OnDestroy {
  data!: PaginatedResponse<unknown>;
  page = 1;
  identifier = 'default';
  recipient?: Recipient;
  webhookResendEnabled = false;

  columns: GateweeTableColumn[] = [];

  dataKeys: {label: string; value: string}[] = [];

  selectedKeyForWebhook: string | {label: string; value: string} = '';

  searchTerm: ProviderTransactionSearchTerm = 'transactionId';

  searchValue = '';

  readonly searchTermOptions: {
    value: ProviderTransactionSearchTerm;
    label: string;
  }[] = [
    {
      value: 'e2eId',
      label: 'features.recipients.providerTransactions.search.e2eId',
    },
    {
      value: 'transactionId',
      label: 'features.recipients.providerTransactions.search.transactionId',
    },
    {
      value: 'externalId',
      label: 'features.recipients.providerTransactions.search.externalId',
    },
    {
      value: 'idempotencyKey',
      label: 'features.recipients.providerTransactions.search.idempotencyKey',
    },
  ];

  private stateSub?: Subscription;

  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<
      void,
      GateweeTableModalDialogData
    >,
  ) {}

  ngOnInit(): void {
    this.stateSub = this.context.data.state$.subscribe(state => {
      this.data = state.data;
      this.page = state.page;
      this.recipient = state.recipient;
      this.identifier = state.identifier;
      this.webhookResendEnabled = state.webhookResendEnabled;
      this.buildColumnsAndKeys();
    });
  }

  ngOnDestroy(): void {
    this.stateSub?.unsubscribe();
  }

  dismiss(): void {
    this.context.completeWith();
  }

  private buildColumnsAndKeys(): void {
    if (!this.data?.records?.length) {
      this.columns = [];
      this.dataKeys = [];
      this.selectedKeyForWebhook = '';
      return;
    }
    const sample = this.data.records[0] as Record<string, unknown>;
    const keys = Object.keys(sample);
    this.dataKeys = keys.map(key => ({label: key, value: key}));
    this.columns = keys.map(key => ({
      label: key,
      field: key,
      sortable: true,
      visible: true,
      alwaysVisible: true,
      class: 'gw-text-text-primary',
    }));
    if (
      this.identifier === 'provider-transactions' &&
      this.webhookResendEnabled
    ) {
      this.columns.push({
        label: 'features.recipients.providerTransactions.resendWebhook',
        field: 'actions',
        visible: true,
        alwaysVisible: true,
        actions: [
          {
            key: 'resendWebhook',
            label: 'features.recipients.providerTransactions.resendWebhook',
            icon: '@phi.arrows-clockwise',
          },
        ],
      });
    }
    if (
      (this.selectedKeyForWebhook === '' ||
        (typeof this.selectedKeyForWebhook === 'object' &&
          !this.selectedKeyForWebhook?.value)) &&
      keys.length > 0
    ) {
      const defaultKey = keys.includes('transactionId')
        ? 'transactionId'
        : keys[0];
      this.selectedKeyForWebhook =
        this.dataKeys.find(k => k.value === defaultKey) ?? this.dataKeys[0];
    }
  }

  pageChange(e: string): void {
    this.context.data.onNewPage(e);
  }

  onRowAction(event: {row: unknown; type: string}): void {
    if (event.type === 'resendWebhook') {
      const raw = this.selectedKeyForWebhook;
      const selectedKey =
        typeof raw === 'string' ? raw : (raw as {value?: string})?.value ?? '';
      this.context.data.onResendWebhook({
        row: event.row as Record<string, unknown>,
        selectedKey,
      });
    }
  }

  onSearchApplyClick(): void {
    this.context.data.onSearchApply({
      term: this.searchTerm,
      value: this.searchValue.trim(),
    });
  }

  onSearchClear(): void {
    this.searchValue = '';
    this.context.data.onSearchApply({term: this.searchTerm, value: ''});
  }

  get isProviderTransactions(): boolean {
    return this.identifier === 'provider-transactions' && !!this.recipient;
  }
}
